import type { ComponentType, JSX, SVGProps } from 'react';
import { motion } from 'framer-motion';
import { revealUp, revealItem, staggerParent, inViewOnce, useRevealGate } from './anim';
import { CreateRoomButton } from './CreateRoomButton';
import { SectionGlow } from './SectionGlow';
import { IconRoom, IconLink, IconJoin, IconLaunch } from './icons';

const STEPS: { n: string; Icon: ComponentType<SVGProps<SVGSVGElement>>; title: string; line: string }[] = [
  { n: '1', Icon: IconRoom, title: 'Create a room', line: 'Pick a name and your character. Your world is ready in seconds — no setup.' },
  { n: '2', Icon: IconLink, title: 'Share the link', line: 'Send one link to your team. That link IS the door to your space.' },
  { n: '3', Icon: IconJoin, title: 'Friends join', line: 'They tap in as a guest — no account, no download. Just walk through.' },
  { n: '4', Icon: IconLaunch, title: 'Work together', line: 'Talk, share, build, celebrate. Spend the day somewhere you enjoy.' },
];

/**
 * How it works — four steps with big numbered tokens and an animated connector line. Minimal text, strong
 * visuals. The connector is a pure CSS gradient bar (decorative, aria-hidden). Closes with a Create Room CTA.
 */
export function HowItWorksSection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section id="how-it-works" aria-labelledby="how-heading" className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
      <SectionGlow tone="warm" side="left" />
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">Up and running fast</p>
        <h2 id="how-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Four steps to your own world
        </h2>
      </motion.div>

      <div className="relative mt-16">
        {/* Connector line behind the steps (desktop only) — a soft amber gradient threading the numbers. */}
        <div
          aria-hidden="true"
          className="absolute left-0 right-0 top-8 hidden h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent lg:block"
        />
        <motion.ol
          variants={reduce ? undefined : staggerParent}
          initial={reduce ? false : 'hidden'}
          whileInView={reduce ? undefined : 'show'}
          viewport={inViewOnce}
          className="relative grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4"
        >
          {STEPS.map((s) => (
            <motion.li key={s.n} variants={reduce ? undefined : revealItem} className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-amber-300/30 bg-[#0b1020] text-amber-300 shadow-[0_0_30px_-8px_rgba(255,210,63,0.5)]">
                <s.Icon className="h-7 w-7" />
              </div>
              <div className="mt-4 text-xs font-bold uppercase tracking-widest text-amber-300/70">Step {s.n}</div>
              <h3 className="mt-1 text-lg font-bold text-white">{s.title}</h3>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/60">{s.line}</p>
            </motion.li>
          ))}
        </motion.ol>
      </div>

      <div className="mt-12 flex justify-center">
        <CreateRoomButton size="md" />
      </div>
    </section>
  );
}
